/* eslint-disable react/no-array-index-key */
import React, { FunctionComponent, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { partial } from 'lodash/fp';
import { cloneDeep, set, sum } from 'lodash';
import { Alert } from 'react-bootstrap';
import { fmtMoney } from '@upstart/harmony/lib/formatter';
import * as changeLogHelper from '@upstart/harmony/lib/deskingoffer-changelog';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { FormattedDTE } from '@upstart/harmony/lib/legos/dtEntitiesTypes';
import { isEmbeddedInCommandCenter } from 'util/commandCenter';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { FinanceType } from '@upstart/harmony/lib/legos/constants';
import { Action, ModalType } from 'util/amplitudeEvents';

import DataTable from '../../DataTable';
import Input, { parseFloatValue, PriceInput } from '../../PencilEditor/Input';
import {
  ADDONS_MODAL_HEADERS,
  ADDONS_MODAL_HEADERS_WITH_DEALERTRACK_MATCH_CODE,
  ADDONS_MODAL_HEADERS_WITH_DMS_CODE,
  ALLOW_TAXABLE_ADDONS_TOGGLE_STATES,
  NEW_ADD_ONS_ROW,
  scrollableModalStyles,
} from '../../../util/constants';
import './AddOnsModal.scss';
import {
  getDteCostAsFloat,
  isDealerTrackDMSConfigured,
  isDMSFlowEnabled as isDMSFlowEnabledUtil,
} from '../../../util/dmsUtils';
import dtEntitiesActions from '../../../redux/dtEntities/actions';
import { getDmsInput } from './DmsInput';
import { determineTargetState } from '../../../util/deskingUtils';

export interface AddOn {
  dmsCode?: string;
  dtEntityId?: string;
  description: string;
  price: number;
  cost: number;
  taxed: boolean;
}

interface DteResponse {
  dtEntities: FormattedDTE[];
}

interface Props {
  offer: DeskingOffer;
  dealer: Dealer;
  disabled: boolean;
  isOpen: boolean;
  closeModal: () => void;
  onChange: (offer: DeskingOffer) => void;
  actions: ReturnType<typeof bindActionCreators>;
  dealerTrackEntities: DteResponse;
  deal: Deal;
  sendAmplitudeEvent?: (event: ModalType, action: Action) => void;
}

export function getTableHeaders(
  isDMSFlowEnabled: boolean,
  isDealerTrackEnabled: boolean,
) {
  if (!isDMSFlowEnabled) {
    return ADDONS_MODAL_HEADERS;
  }

  if (isDealerTrackEnabled) {
    return ADDONS_MODAL_HEADERS_WITH_DEALERTRACK_MATCH_CODE;
  }

  return ADDONS_MODAL_HEADERS_WITH_DMS_CODE;
}

const AddOnsModal: FunctionComponent<Props> = props => {
  const { actions, dealer, sendAmplitudeEvent } = props;

  const dtEntities = (props.dealerTrackEntities || {}).dtEntities || [];
  const dteMap = new Map<string, FormattedDTE>(
    dtEntities.map(dte => [dte.id, dte]),
  );

  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // most states do not account for add ons in lease tax calculation, so as to not confuse users we disable the ability to toggle the taxed column
  const taxState = determineTargetState(props.deal, props.dealer, props.offer);
  const shouldIgnoreTaxColumn =
    props.offer.data?.financeType === FinanceType.LEASE &&
    !!taxState &&
    !ALLOW_TAXABLE_ADDONS_TOGGLE_STATES.includes(taxState);

  useEffect(() => {
    if (isDealerTrackDMSConfigured(dealer)) {
      actions.dtEntityRequest({ dealerId: dealer.id });
    }
  }, []);

  useEffect(() => {
    if (props.isOpen && props.offer) {
      // When the modal opens, set the local state
      setAddOns(cloneDeep(props.offer.data?.addOns ?? []));
    }
  }, [props.isOpen, props.offer]);

  const changeAddOnsProperty = (
    index: number,
    property: string,
    value: any,
  ) => {
    set(addOns, [index, property], value);
    setAddOns([...addOns]);
  };

  const onDMSCodeChange = (i: number) => (event: any) =>
    changeAddOnsProperty(i, 'dmsCode', event);

  const onDescriptionChange = (i: number) => (event: any) =>
    changeAddOnsProperty(i, 'description', event);

  const onPriceChange = (i: number) => (event: any) =>
    changeAddOnsProperty(i, 'price', parseFloatValue(event));

  const onCostChange = (i: number) => (event: any) =>
    changeAddOnsProperty(i, 'cost', parseFloatValue(event));

  const onTaxedChange = (i: number) =>
    changeAddOnsProperty(i, 'taxed', !addOns[i].taxed);

  const onDteChange = (index: number, dte: FormattedDTE | null) => {
    if (!dte) {
      // if no dte, remove it from the addOn
      changeAddOnsProperty(index, 'dtEntityId', undefined);
      return;
    }

    const addOn = addOns[index];
    changeAddOnsProperty(index, 'dtEntityId', dte.id);

    // if the cost is 0 and the dte has a cost, set the cost on the addOn
    if (addOn.cost === 0 && dte.cost) {
      // dte.cost is a string like "$123.45" so we need to parse it as a float
      changeAddOnsProperty(index, 'cost', getDteCostAsFloat(dte));
    }
  };

  const addAnotherRow = () =>
    setAddOns([...addOns, ...cloneDeep(NEW_ADD_ONS_ROW)]);

  const deleteRow = (index: number) =>
    setAddOns([...addOns.slice(0, index), ...addOns.slice(index + 1)]);

  const saveAddOns = () => {
    // validate addons
    const currentValidationErrors = [];

    // all addons must have a description
    if (addOns.find(addOn => !addOn.description) !== undefined) {
      currentValidationErrors.push('All Add Ons must have a description');
    }

    setValidationErrors(currentValidationErrors);
    if (currentValidationErrors.length > 0) {
      return;
    }

    // save addons
    const newOffer = cloneDeep(props.offer);
    const newChangeLog = changeLogHelper.updateAddOns(newOffer, addOns);
    set(newOffer, ['data', 'addOns'], addOns);
    set(newOffer, 'changeLog', newChangeLog);
    props.onChange(newOffer);
    if (sendAmplitudeEvent) {
      sendAmplitudeEvent(ModalType.ADD_ONS, Action.FINISH);
    }
    props.closeModal();
  };

  const isDMSFlowEnabled = isDMSFlowEnabledUtil(dealer);
  const isDealerTrackEnabled = isDealerTrackDMSConfigured(dealer);
  const tableHeaders = getTableHeaders(isDMSFlowEnabled, isDealerTrackEnabled);

  const rows = addOns.map((addOn, i) => {
    const dmsInput = getDmsInput({
      isDealerTrackEnabled,
      isDMSFlowEnabled,
      addOn,
      index: i,
      dtEntities,
      dteMap,
      onDMSCodeChange,
      onDteChange,
      disabled: props.disabled,
    });

    return [
      <Input
        key={i}
        onChange={onDescriptionChange(i)}
        value={addOn.description}
        disabled={props.disabled}
      />,
      dmsInput,
      <PriceInput
        key={i}
        value={addOn.price}
        onChange={onPriceChange(i)}
        disabled={props.disabled}
      />,
      <PriceInput
        key={i}
        value={addOn.cost}
        onChange={onCostChange(i)}
        disabled={props.disabled}
      />,
      <Input
        key={i}
        onChange={partial(onTaxedChange, [i])}
        checked={addOn.taxed}
        type="checkbox"
        disabled={shouldIgnoreTaxColumn ? true : props.disabled}
      />,
      <button
        key={i}
        className="modal-button delete"
        onClick={() => deleteRow(i)}
        disabled={props.disabled}
      >
        Delete
      </button>,
    ];
  });

  const totalPrice = sum(addOns.map(addOn => addOn.price));
  const totalCost = sum(addOns.map(addOn => addOn.cost));

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={() => {
        if (sendAmplitudeEvent) {
          sendAmplitudeEvent(ModalType.ADD_ONS, Action.CANCEL);
        }
        props.closeModal();
      }}
      contentLabel="AddOns Modal"
      style={scrollableModalStyles as any}
    >
      <span
        className="close"
        onClick={() => {
          if (sendAmplitudeEvent) {
            sendAmplitudeEvent(ModalType.ADD_ONS, Action.CANCEL);
          }
          props.closeModal();
        }}
      >
        X
      </span>
      <h2>Add Ons</h2>
      {(validationErrors.length && (
        <Alert bsStyle="danger">
          {validationErrors.map(error => (
            <div key={error}>{error}</div>
          ))}
        </Alert>
      )) ||
        null}
      <DataTable
        className="addons-modal-table"
        emptyMessage="No add ons set"
        rows={rows}
        headers={tableHeaders}
      />
      <div className="row">
        <div className="total-value">
          total price: {fmtMoney(totalPrice, 2)}
        </div>
        <div className="total-value">total cost: {fmtMoney(totalCost, 2)}</div>
      </div>
      {!props.disabled && (
        <div className="modal-footer">
          <button
            className="modal-button cc-button-outlined"
            onClick={addAnotherRow}
          >
            Add more
          </button>
          <button
            className="modal-button cc-button-primary"
            onClick={saveAddOns}
          >
            {isEmbeddedInCommandCenter() ? 'Save' : 'Save new add ons'}
          </button>
        </div>
      )}
    </Modal>
  );
};

const mapStateToProps = ({ dtEntities }: { dtEntities: DteResponse }) => ({
  dealerTrackEntities: dtEntities,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators(
    {
      ...dtEntitiesActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddOnsModal);
